import React, { useState, useEffect, useMemo } from 'react';
import { groupCRMUsersByDTC } from '../../utils/crmUserSorting';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  IconButton,
  Tooltip,
  Text,
  useColorModeValue,
  Select as ChakraSelect,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Badge,
  Divider,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FiArrowLeft, FiSettings, FiCalendar, FiPhone, FiBell, FiVideo, FiLink, FiDatabase, FiEye, FiUsers } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import CalendarSyncSettings from '../../components/CRM/Settings/CalendarSyncSettings';
import MeetingProviderSettings from '../../components/CRM/Settings/MeetingProviderSettings';
import DialpadSettings from '../../components/CRM/Settings/DialpadSettings';
import NotificationSettings from '../../components/CRM/Settings/NotificationSettings';
import AriveSettings from '../../components/CRM/Settings/AriveSettings';
import AriveAdminPanel from '../../components/CRM/Arive/AriveAdminPanel';
import FieldMappingManager from '../../components/CRM/Arive/FieldMappingManager';
import CrmViewPreferences from '../../components/CRM/Settings/CrmViewPreferences';
import MyTeamSettings from '../../components/CRM/Settings/MyTeamSettings';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';

export default function SettingsPage() {
  const history = useHistory();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const { teamSharing } = useCrmSettings();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [crmUsers, setCrmUsers] = useState([]);
  const [targetUser, setTargetUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  // Multi-owner rollout flag: the My Team tab is admin-only until self-serve
  // sharing is enabled broadly (teamSharing.selfServe) or the user is piloting.
  // manageCrmTeam enforces the same rule server-side.
  const canSeeMyTeamTab =
    isCRMAdmin ||
    teamSharing?.selfServe === true ||
    (Array.isArray(teamSharing?.pilotOwnerUids) &&
      teamSharing.pilotOwnerUids.includes(currentUser?.uid));
  const effectiveUserId = selectedUserId || currentUser?.uid;
  const isViewingOtherUser = selectedUserId && selectedUserId !== currentUser?.uid;

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const boxBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // FeatureTour: named actions the settings tours can invoke — click the
  // tagged tab header, never index math (this tab set is role-conditional,
  // so indices shift between admins and LOs). Must stay above the early return.
  useTourActions(useMemo(() => {
    const clickTab = (id) => document.querySelector(`[data-tour="${id}"]`)?.click();
    return {
      showDefaultViewTab: () => clickTab('settings-tab-defaultview'),
      showCalendarTab: () => clickTab('settings-tab-calendar'),
      showMeetingTab: () => clickTab('settings-tab-meeting'),
      showAriveTab: () => clickTab('settings-tab-arive'),
      showNotificationsTab: () => clickTab('settings-tab-notifications'),
      showMappingsTab: () => clickTab('settings-tab-mappings'),
    };
  }, []));

  // Load CRM users for admin filter
  useEffect(() => {
    if (!isCRMAdmin) {
      setLoading(false);
      return;
    }

    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('CRMUser', '==', true));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          uid: doc.id,
          name: userData.Name || userData.name || userData.displayName || userData.email,
          email: userData.Email || userData.email,
          ...userData,
        });
      });
      setCrmUsers(users);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isCRMAdmin]);

  // Load target user data
  useEffect(() => {
    if (!effectiveUserId) return;

    const userRef = doc(db, 'users', effectiveUserId);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        setTargetUser({ uid: docSnap.id, ...docSnap.data() });
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading user data:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [effectiveUserId]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">Loading settings...</Text>
        </Center>
      </Container>
    );
  }

  const getPageTitle = () => {
    if (isViewingOtherUser) {
      const user = crmUsers.find(u => u.uid === selectedUserId);
      return `${user?.name || 'User'}'s Settings`;
    }
    return 'My Settings';
  };

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="100%" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={6} maxW="1400px" mx="auto">
          {/* Header */}
          <Box bg={headerBG} p={6} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3}>
                <Tooltip label="Back to My Deals">
                  <IconButton
                    icon={<FiArrowLeft />}
                    aria-label="Back to My Deals"
                    onClick={() => history.push('/crm/my-deals')}
                    size="lg"
                    variant="ghost"
                  />
                </Tooltip>
                <VStack align="flex-start" spacing={1} data-tour="settings-header">
                  <HStack spacing={2}>
                    <Icon as={FiSettings} boxSize={6} color="blue.500" />
                    <Heading size="lg">{getPageTitle()}</Heading>
                  </HStack>
                  <Text color="gray.600" fontSize="sm">
                    {isViewingOtherUser 
                      ? 'Manage user settings and integrations'
                      : 'Customize your CRM experience and integrations'}
                  </Text>
                </VStack>
              </HStack>

              {/* User Filter for Admins */}
              {isCRMAdmin && (
                <VStack align="flex-end" spacing={2}>
                  <FormControl maxW="300px" data-tour="settings-admin-picker">
                    <FormLabel fontSize="xs" mb={1} textTransform="uppercase" fontWeight="semibold" color="gray.600">
                      Edit Settings For
                    </FormLabel>
                    <ChakraSelect
                      size="sm"
                      placeholder="My Settings"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      {(() => {
                        const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                        return (
                          <>
                            {dtcUsers.length > 0 && (
                              <optgroup label="DTC Loan Officers">
                                {dtcUsers.map((user) => (
                                  <option key={user.uid} value={user.uid}>{user.name}</option>
                                ))}
                              </optgroup>
                            )}
                            <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                              {otherUsers.map((user) => (
                                <option key={user.uid} value={user.uid}>{user.name}</option>
                              ))}
                            </optgroup>
                          </>
                        );
                      })()}
                    </ChakraSelect>
                  </FormControl>
                  {isViewingOtherUser && (
                    <Badge colorScheme="purple" fontSize="sm">
                      Admin Mode
                    </Badge>
                  )}
                </VStack>
              )}
            </HStack>

            {isViewingOtherUser && (
              <Box mt={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                <HStack spacing={2}>
                  <Icon as={FiSettings} color="blue.600" />
                  <Text fontSize="sm" color="blue.800">
                    You are editing settings for{' '}
                    <Text as="span" fontWeight="bold">
                      {targetUser?.name || targetUser?.email || 'this user'}
                    </Text>
                    {' '}({targetUser?.email || 'no email'})
                  </Text>
                </HStack>
              </Box>
            )}
          </Box>

          {/* Settings Tabs */}
          <Box bg={boxBG} borderRadius="xl" shadow="sm">
            <Tabs colorScheme="blue" isLazy>
              <TabList px={6} pt={4} data-tour="settings-tabs">
                {isCRMAdmin && (
                  <Tab data-tour="settings-tab-defaultview">
                    <HStack spacing={2}>
                      <Icon as={FiEye} />
                      <Text>Default View</Text>
                    </HStack>
                  </Tab>
                )}
                {canSeeMyTeamTab && (
                  <Tab data-tour="settings-tab-team">
                    <HStack spacing={2}>
                      <Icon as={FiUsers} />
                      <Text>My Team</Text>
                    </HStack>
                  </Tab>
                )}
                <Tab data-tour="settings-tab-calendar">
                  <HStack spacing={2}>
                    <Icon as={FiCalendar} />
                    <Text>Calendar Sync</Text>
                  </HStack>
                </Tab>
                <Tab data-tour="settings-tab-meeting">
                  <HStack spacing={2}>
                    <Icon as={FiVideo} />
                    <Text>Meeting Providers</Text>
                  </HStack>
                </Tab>
                {(isCRMAdmin || targetUser?.dialpadCapable) && (
                  <Tab data-tour="settings-tab-dialpad">
                    <HStack spacing={2}>
                      <Icon as={FiPhone} />
                      <Text>Dialpad</Text>
                    </HStack>
                  </Tab>
                )}
                {/* Booking Pages moved to /crm/integrations (FHL Scheduling tab) */}
                {/* Arive LOS temporarily admin-only — hidden for normal users */}
                {isCRMAdmin && (
                  <Tab data-tour="settings-tab-arive">
                    <HStack spacing={2}>
                      <Icon as={FiLink} />
                      <Text>Arive LOS</Text>
                    </HStack>
                  </Tab>
                )}
                {/* Notifications temporarily admin-only — hidden for normal users */}
                {isCRMAdmin && (
                  <Tab data-tour="settings-tab-notifications">
                    <HStack spacing={2}>
                      <Icon as={FiBell} />
                      <Text>Notifications</Text>
                    </HStack>
                  </Tab>
                )}
                {isCRMAdmin && (
                  <Tab data-tour="settings-tab-mappings">
                    <HStack spacing={2}>
                      <Icon as={FiDatabase} />
                      <Text>Field Mappings</Text>
                    </HStack>
                  </Tab>
                )}
              </TabList>

              <Divider />

              <TabPanels>
                {/* Default View Tab (Admin only) */}
                {isCRMAdmin && (
                  <TabPanel px={6} py={6} data-tour="settings-defaultview">
                    <CrmViewPreferences userId={effectiveUserId} targetUser={targetUser} />
                  </TabPanel>
                )}

                {/* My Team Tab (book sharing) — gated by the teamSharing rollout flag */}
                {canSeeMyTeamTab && (
                  <TabPanel px={6} py={6} data-tour="settings-team">
                    <MyTeamSettings userId={effectiveUserId} isAdmin={isCRMAdmin} />
                  </TabPanel>
                )}

                {/* Calendar Sync Tab */}
                <TabPanel px={6} py={6} data-tour="settings-calendar">
                  <CalendarSyncSettings userId={effectiveUserId} />
                </TabPanel>

                {/* Meeting Providers Tab */}
                <TabPanel px={6} py={6} data-tour="settings-meeting">
                  <MeetingProviderSettings userId={effectiveUserId} />
                </TabPanel>

                {/* Dialpad Tab */}
                {(isCRMAdmin || targetUser?.dialpadCapable) && (
                  <TabPanel px={6} py={6}>
                    <DialpadSettings 
                      userId={effectiveUserId} 
                      isAdmin={isCRMAdmin}
                    />
                  </TabPanel>
                )}

                {/* Arive LOS Tab (temporarily admin-only) */}
                {isCRMAdmin && (
                  <TabPanel px={6} py={6}>
                    <VStack align="stretch" spacing={6}>
                      <Box data-tour="settings-arive">
                        <AriveSettings userId={effectiveUserId} isAdmin={isCRMAdmin} />
                      </Box>
                      <Box data-tour="settings-arive-admin">
                        <AriveAdminPanel />
                      </Box>
                    </VStack>
                  </TabPanel>
                )}

                {/* Notifications Tab (temporarily admin-only) */}
                {isCRMAdmin && (
                  <TabPanel px={6} py={6} data-tour="settings-notifications">
                    <NotificationSettings userId={effectiveUserId} />
                  </TabPanel>
                )}

                {/* Field Mappings Tab (Admin only) */}
                {isCRMAdmin && (
                  <TabPanel px={6} py={6} data-tour="settings-mappings">
                    <FieldMappingManager />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}

